<script lang="ts" setup>
import KnowThat from '@/domains/products/products-multibilet/know-that/know-that.vue'
import IndividualPath from '@/domains/products/products-multibilet/individual-path/individual-path.vue'
import Banner from '@/domains/esg/components/banner/banner.vue'
import Faq from '@/domains/products/products-multibilet/faq/faq.vue'

const { t, locale } = useI18n()

useHead({
  title: t('pageTitles.multibilet.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.multibilet.meta'),
    },
  ],
  link: [
    {
      hid: 'favicon',
      rel: 'icon',
      type: 'image/x-icon',
      href: '/favicon.ico',
    },
    {
      rel: 'canonical',
      href: t(`pageTitles.multibilet.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.multibilet.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.multibilet.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.multibilet.canonical.pl'),
    },
  ],
})

const columns = [
  { key: 'feature', title: t('multibilet.offerScope.columns.feature'), width: '26rem' },
  {
    key: 'eMultiBilet',
    title: t('multibilet.offerScope.columns.eMultiBilet'),
    align: 'center',
    width: '22rem',
  },
  {
    key: 'eMultiBiletPremium',
    title: t('multibilet.offerScope.columns.eMultiBiletPremium'),
    align: 'center',
    width: '24rem',
  },
]

const items = [
  {
    feature: t('multibilet.offerScope.rows.helios.feature'),
    eMultiBilet: t('multibilet.offerScope.rows.helios.eMultiBilet'),
    eMultiBiletPremium: t('multibilet.offerScope.rows.helios.eMultiBiletPremium'),
  },
  {
    feature: t('multibilet.offerScope.rows.cinemaCity.feature'),
    eMultiBilet: t('multibilet.offerScope.rows.cinemaCity.eMultiBilet'),
    eMultiBiletPremium: t('multibilet.offerScope.rows.cinemaCity.eMultiBiletPremium'),
  },
  {
    feature: t('multibilet.offerScope.rows.multiKino.feature'),
    eMultiBilet: t('multibilet.offerScope.rows.multiKino.eMultiBilet'),
    eMultiBiletPremium: t('multibilet.offerScope.rows.multiKino.eMultiBiletPremium'),
  },
  {
    feature: t('multibilet.offerScope.rows.local.feature'),
    eMultiBilet: t('multibilet.offerScope.rows.local.eMultiBilet'),
    eMultiBiletPremium: t('multibilet.offerScope.rows.local.eMultiBiletPremium'),
  },
]

</script>

<template>
  <div>
    <Hero
      desktop-image="/images/hero/multilife_desktop.png"
      mobile-image="/images/hero/multilife_mobile.png"
      :header="$t('multibilet.hero.header')"
      :text="$t('multibilet.hero.text')"
      :button="{
        text: $t('multibilet.hero.buttonText'),
        to: '#gdzie-zrealizuje-bilet',
      }"
      :button2="{
        text: $t('multibilet.hero.button2Text'),
        to: '#zakres-oferty',
      }"
    />

    <KnowThat />

    <IndividualPath />

    <UiSection
      id="zakres-oferty"
      class="partnerships"
      color="gray"
    >
      <div class="section__container">
        <div class="section__row">
          <div class="section__col">
            <UiHeader
              :header="$t('multibilet.offerScope.header')"
              :position="'center'"
            />

            <UiTable
              :columns="columns"
              :items="items"
            >
              <template #head-eMultiBilet>
                <div class="multi-bilet-table__head">
                  <strong>{{ $t('multibilet.offerScope.headers.eMultiBilet.title') }}</strong>
                  <span v-html="$t('multibilet.offerScope.headers.eMultiBilet.description')" />
                </div>
              </template>

              <template #head-eMultiBiletPremium>
                <div class="multi-bilet-table__head">
                  <strong>{{ $t('multibilet.offerScope.headers.eMultiBiletPremium.title') }}</strong>
                  <span v-html="$t('multibilet.offerScope.headers.eMultiBiletPremium.description')" />
                </div>
              </template>
            </UiTable>
          </div>
        </div>

        <div class="section__row">
          <div class="section__col">
            <p>{{ $t('multibilet.offerScope.legend.unavailable') }}</p>
            <p>{{ $t('multibilet.offerScope.legend.available') }}</p>
          </div>
        </div>
      </div>
    </UiSection>

    <Banner
      id="gdzie-zrealizuje-bilet"
      class="section_banner"
      :heading="$t('multibilet.banner.heading')"
      :text="$t('multibilet.banner.text')"
      color="white"
      background="blue-400"
      :button="{
        text: $t('multibilet.banner.button.text'),
        to: $t('multibilet.banner.button.link'),
        target: '_blank'
      }"
    />

    <Faq />
    
  </div>
</template>

<style scoped lang="scss">

:deep(.ui-header) {
   @include header-investor();
}

:deep(.section_banner) {
  min-height: auto;
}

:deep(.banner__title) {
  width: 100%;
  font-size: 3.2rem !important;
  line-height: 4rem !important;

  @include atM {
    font-size: 6rem !important;
    line-height: 6.8rem !important;
  }
}

:deep(.banner__text) {
  width: 100%;
  font-size: 1.6rem !important;
  line-height: 2.6rem !important;

  @include atM {
    font-size: 2.2rem !important;
    line-height: 3.2rem !important;
  }
}

:deep(.text-with-boxes__content .box__image) {
  height: auto;
  text-align: center;
}

:deep(.text-with-boxes__content .box__image img) {
  width: auto;
  height: auto;
  object-fit: contain;
}

.multi-bilet-table__head {
  display: grid;
  gap: 0.4rem;
  text-align: center;

  strong {
    font-weight: 700;
  }

  span {
    display: block;
    font-weight: 400;
    font-size: 1.2rem;
    line-height: 1.6rem;
  }
}
</style>
